import { apiFetch, jsonOpts, parseJson } from "./client";
import type { components } from "./schema";
import type {
  CustomerAccount,
  CustomerAccountDetail,
  CustomerAccountListItem,
  CustomerContact,
  CustomerLicense,
  Equipment,
  Field,
} from "./types";

export type CreateCustomerContactInput =
  components["schemas"]["CustomerContactIn"];
export type UpdateCustomerContactInput =
  components["schemas"]["CustomerContactUpdateIn"];
export type CreateFieldInput = components["schemas"]["FieldIn"];
export type UpdateFieldInput = components["schemas"]["FieldUpdateIn"];
export type CreateEquipmentInput = components["schemas"]["EquipmentIn"];
export type UpdateEquipmentInput = components["schemas"]["EquipmentUpdateIn"];

export type CreateCustomerLicenseInput =
  components["schemas"]["CustomerLicenseIn"];
export type UpdateCustomerLicenseInput =
  components["schemas"]["CustomerLicenseUpdateIn"];

export async function getAccounts(): Promise<CustomerAccountListItem[]> {
  return parseJson<CustomerAccountListItem[]>(
    await apiFetch("/api/customers/accounts")
  );
}

export async function getAccount(accountId: string): Promise<CustomerAccount> {
  return parseJson<CustomerAccount>(
    await apiFetch(`/api/customers/accounts/${accountId}`)
  );
}

export async function getAccountDetail(
  accountId: string
): Promise<CustomerAccountDetail> {
  return parseJson<CustomerAccountDetail>(
    await apiFetch(`/api/customers/accounts/${accountId}/detail`)
  );
}

export async function getFields(accountId: string): Promise<Field[]> {
  return parseJson<Field[]>(
    await apiFetch(`/api/customers/accounts/${accountId}/fields`)
  );
}

export async function getFieldZones(): Promise<string[]> {
  return parseJson<string[]>(await apiFetch("/api/customers/field-zones"));
}

export async function createField(
  accountId: string,
  body: CreateFieldInput
): Promise<Field> {
  return parseJson<Field>(
    await apiFetch(`/api/customers/accounts/${accountId}/fields`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateField(
  accountId: string,
  fieldId: string,
  body: UpdateFieldInput
): Promise<Field> {
  return parseJson<Field>(
    await apiFetch(`/api/customers/accounts/${accountId}/fields/${fieldId}`, {
      ...jsonOpts,
      method: "PATCH",
      body: JSON.stringify(body),
    })
  );
}

export async function deleteField(
  accountId: string,
  fieldId: string
): Promise<void> {
  await parseJson<void>(
    await apiFetch(`/api/customers/accounts/${accountId}/fields/${fieldId}`, {
      ...jsonOpts,
      method: "DELETE",
    })
  );
}

export async function getContacts(
  accountId: string
): Promise<CustomerContact[]> {
  return parseJson<CustomerContact[]>(
    await apiFetch(`/api/customers/accounts/${accountId}/contacts`)
  );
}

export async function createContact(
  accountId: string,
  body: CreateCustomerContactInput
): Promise<CustomerContact> {
  return parseJson<CustomerContact>(
    await apiFetch(`/api/customers/accounts/${accountId}/contacts`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function getContact(
  accountId: string,
  contactId: string
): Promise<CustomerContact> {
  return parseJson<CustomerContact>(
    await apiFetch(`/api/customers/accounts/${accountId}/contacts/${contactId}`)
  );
}

export async function updateContact(
  accountId: string,
  contactId: string,
  body: UpdateCustomerContactInput
): Promise<CustomerContact> {
  return parseJson<CustomerContact>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/contacts/${contactId}`,
      {
        ...jsonOpts,
        method: "PATCH",
        body: JSON.stringify(body),
      }
    )
  );
}

export async function deleteContact(
  accountId: string,
  contactId: string
): Promise<void> {
  await parseJson<void>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/contacts/${contactId}`,
      { ...jsonOpts, method: "DELETE" }
    )
  );
}

export async function getEquipment(accountId: string): Promise<Equipment[]> {
  return parseJson<Equipment[]>(
    await apiFetch(`/api/customers/accounts/${accountId}/equipment`)
  );
}

export async function getEquipmentItem(
  accountId: string,
  equipmentId: string
): Promise<Equipment> {
  return parseJson<Equipment>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/equipment/${equipmentId}`
    )
  );
}

export async function createLicense(
  accountId: string,
  body: CreateCustomerLicenseInput
): Promise<CustomerLicense> {
  return parseJson<CustomerLicense>(
    await apiFetch(`/api/customers/accounts/${accountId}/licenses`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateLicense(
  accountId: string,
  licenseId: string,
  body: UpdateCustomerLicenseInput
): Promise<CustomerLicense> {
  return parseJson<CustomerLicense>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/licenses/${licenseId}`,
      { ...jsonOpts, method: "PATCH", body: JSON.stringify(body) }
    )
  );
}

export async function deleteLicense(
  accountId: string,
  licenseId: string
): Promise<void> {
  await parseJson<void>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/licenses/${licenseId}`,
      { ...jsonOpts, method: "DELETE" }
    )
  );
}

export async function createEquipment(
  accountId: string,
  body: CreateEquipmentInput
): Promise<Equipment> {
  return parseJson<Equipment>(
    await apiFetch(`/api/customers/accounts/${accountId}/equipment`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateEquipment(
  accountId: string,
  equipmentId: string,
  body: UpdateEquipmentInput
): Promise<Equipment> {
  return parseJson<Equipment>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/equipment/${equipmentId}`,
      {
        ...jsonOpts,
        method: "PATCH",
        body: JSON.stringify(body),
      }
    )
  );
}

export async function deleteEquipment(
  accountId: string,
  equipmentId: string
): Promise<void> {
  await parseJson<void>(
    await apiFetch(
      `/api/customers/accounts/${accountId}/equipment/${equipmentId}`,
      { ...jsonOpts, method: "DELETE" }
    )
  );
}
